import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import Footer from '../components/Footer';
import LanguageToggle from '../components/LanguageToggle';
import { useLanguage } from '../context/LanguageContext';
import styles from './OnboardingPage.module.css';
import Navbar, { BrandIcon } from '../components/Navbar';
import { getApiUrl } from '../api';

const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC'];

const OnboardingPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const initialAuditId = location?.state?.auditRequestId || null;
  const [auditRequestId] = useState(initialAuditId);
  const [tone, setTone] = useState('professional');
  const [unique, setUnique] = useState('');
  const [avoid, setAvoid] = useState('');
  const [plan, setPlan] = useState('pro');
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  // Onboarding requires a signed-in account (it leads straight to a paid
  // checkout). Anyone without a token gets bounced to the homepage login
  // modal, which sends them right back here - with this same auditRequestId -
  // once they sign up or log in.
  useEffect(() => {
    const token = localStorage.getItem('token1');
    if (token) return;
    const params = new URLSearchParams({ login: 'true', redirect: '/onboarding' });
    if (auditRequestId) params.set('auditRequestId', auditRequestId);
    navigate(`/?${params.toString()}`, { replace: true });
  }, [auditRequestId, navigate]);

  useEffect(() => {
    // If we have an auditRequestId, fetch its current data to pre-fill onboarding
    const fetchAudit = async () => {
      if (!auditRequestId) return;
      try {
        const res = await fetch(getApiUrl(`/api/audit/${auditRequestId}`));
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          toast.error(err.message || 'Failed to load audit data');
          return;
        }
        const data = await res.json();
        const onboard = data.auditRequest?.onboarding;
        if (onboard) {
          if (onboard.tone) setTone(onboard.tone);
          if (onboard.uniqueText) setUnique(onboard.uniqueText);
          if (onboard.avoidText) setAvoid(onboard.avoidText);
          if (onboard.plan) setPlan(onboard.plan);
        }
      } catch (err) {
        console.error('Fetch audit error', err);
        toast.error('Network error while loading audit');
      }
    };
    fetchAudit();
  }, [auditRequestId]);

  const validateFields = () => {
    const newErrors = {};
    if (!unique.trim()) newErrors.unique = 'Please tell us what makes your business unique.';
    if (!avoid.trim()) newErrors.avoid = 'Please tell us what we should avoid saying.';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const saveOnboarding = async () => {
    if (!validateFields()) {
      toast.error('Please fill in all required fields before continuing.');
      return;
    }
    // auditRequestId is optional here - a user may reach onboarding without
    // ever running an audit (e.g. from the pricing page). In that case the
    // backend stores onboarding answers directly on the User record instead
    // of an AuditRequest, and the user can connect their Google Business
    // locations later from the dashboard.
    const token = localStorage.getItem('token1');
    if (!token) {
      // Token was cleared/expired since the page loaded (e.g. another tab
      // logged out) - send them back through login rather than failing silently.
      const params = new URLSearchParams({ login: 'true', redirect: '/onboarding' });
      if (auditRequestId) params.set('auditRequestId', auditRequestId);
      navigate(`/?${params.toString()}`, { replace: true });
      return;
    }

    setSaving(true);
    try {
      const res = await fetch(getApiUrl('/api/audit/save-onboarding'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ auditRequestId, tone, uniqueText: unique, avoidText: avoid, plan }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success('Onboarding saved');
        navigate('/payment', { state: { auditRequestId, plan } });
        return;
      }
      if (res.status === 401) {
        localStorage.removeItem('token1');
        localStorage.removeItem('user1');
        const params = new URLSearchParams({ login: 'true', redirect: '/onboarding' });
        if (auditRequestId) params.set('auditRequestId', auditRequestId);
        toast.error('Your session expired - please log in again.');
        navigate(`/?${params.toString()}`, { replace: true });
        return;
      }
      toast.error(data.message || 'Failed to save onboarding');
    } catch (err) {
      console.error('Save onboarding error', err);
      toast.error('Network error. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.page}>
      <Navbar className={styles.navbar} innerClassName={styles.navInner} brandClassName={styles.navBrand} brand={<><BrandIcon size={24} style={{ marginRight: 6 }} />ReplyShoot</>}>
          <LanguageToggle />
      </Navbar>

      <main className={styles.onboardMain}>
        <section className={styles.toneCardWrap}>
          <h2 className={styles.onboardTitle}>{t('howShouldWeSound', 'How should we sound?')}</h2>
          <p className={styles.onboardSub}>{t('matchBrandVoice', "We'll match your brand voice in every response.")}</p>

          <div className={styles.card}>
            <div className={styles.toneGrid}>
              <button type="button" className={`${styles.toneCard} ${tone==='professional'?styles.toneActive:''}`} onClick={() => setTone('professional')}>
                <div className={styles.toneTitle}>{t('toneProfessional', 'Professional')}</div>
                <div className={styles.toneDesc}>{t('toneProfessionalDesc', 'Polished and business-like. Great for medical, legal, and financial services.')}</div>
              </button>
              <button type="button" className={`${styles.toneCard} ${tone==='friendly'?styles.toneActive:''}`} onClick={() => setTone('friendly')}>
                <div className={styles.toneTitle}>{t('toneFriendly', 'Friendly & Warm')}</div>
                <div className={styles.toneDesc}>{t('toneFriendlyDesc', 'Approachable and conversational. Great for restaurants, salons, and retail.')}</div>
              </button>
              <button type="button" className={`${styles.toneCard} ${tone==='casual'?styles.toneActive:''}`} onClick={() => setTone('casual')}>
                <div className={styles.toneTitle}>{t('toneCasual', 'Casual')}</div>
                <div className={styles.toneDesc}>{t('toneCasualDesc', 'Relaxed and laid-back. Great for cafes, fitness studios, and creative businesses.')}</div>
              </button>
              <button type="button" className={`${styles.toneCard} ${tone==='empathetic'?styles.toneActive:''}`} onClick={() => setTone('empathetic')}>
                <div className={styles.toneTitle}>{t('toneEmpathetic', 'Empathetic & Caring')}</div>
                <div className={styles.toneDesc}>{t('toneEmpatheticDesc', 'Sensitive and understanding. Great for healthcare, wellness, and childcare.')}</div>
              </button>
            </div>

            <label className={styles.fieldLabel}>
              {t('uniqueQuestion', 'What makes your business unique?')} <span className={styles.requiredStar}>*</span>
            </label>
            <textarea
              className={`${styles.textarea} ${errors.unique ? styles.textareaError : ''}`}
              placeholder="e.g. Family-owned for 20 years, we use locally sourced ingredients..."
              value={unique}
              onChange={e => { setUnique(e.target.value); if (errors.unique) setErrors(prev => ({ ...prev, unique: '' })); }}
            />
            {errors.unique && <p className={styles.fieldError}>{errors.unique}</p>}

            <label className={styles.fieldLabel}>
              {t('avoidQuestion', 'Anything we should avoid saying?')} <span className={styles.requiredStar}>*</span>
            </label>
            <textarea
              className={`${styles.textarea} ${errors.avoid ? styles.textareaError : ''}`}
              placeholder="e.g. Don't mention specific pricing, don't offer free items..."
              value={avoid}
              onChange={e => { setAvoid(e.target.value); if (errors.avoid) setErrors(prev => ({ ...prev, avoid: '' })); }}
            />
            {errors.avoid && <p className={styles.fieldError}>{errors.avoid}</p>}

            <div className={styles.cardActions}>
              <button
                type="button"
                className={styles.primaryBtn}
                onClick={() => {
                  if (!validateFields()) return;
                  window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
                }}
              >
                {t('continueToPlan', 'Continue to Plan Selection')}
              </button>
            </div>
          </div>
        </section>

        <section className={styles.planSection}>
          <h3 className={styles.planTitle}>{t('chooseYourPlan', 'Choose your plan')}</h3>
          <p className={styles.planSub}>{t('noContractsNoFees', 'Cancel anytime. No contracts. No setup fees.')}</p>

          <div className={styles.plansGrid}>
            <button type="button" className={`${styles.planCard} ${plan==='starter'?styles.planSelected:''}`} onClick={() => setPlan('starter')}>
              <div className={styles.planName}>Starter</div>
              <div className={styles.planPrice}>$5/mo</div>
              <div className={styles.planDetail}>{t('planStarterDetails', '1 location, Google reviews, 48hr response')}</div>
            </button>

            <button type="button" className={`${styles.planCard} ${plan==='pro'?styles.planSelected:''}`} onClick={() => setPlan('pro')}>
              <div className={styles.planName}>Pro <span className={styles.mostPopular}>{t('mostPopular', 'Most Popular')}</span></div>
              <div className={styles.planPrice}>$10/mo</div>
              <div className={styles.planDetail}>{t('planProDetails', 'Multiple platforms, priority response time, advanced personalization')}</div>
            </button>
          </div>

          <div className={styles.planActions}>
            <button type="button" className={styles.ctaBtn} onClick={saveOnboarding} disabled={saving}>
              {saving ? t('savingText', 'Saving...') : t('continueToPayment', 'Continue to Payment')}
            </button>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default OnboardingPage;
